import { useEffect, useState } from 'react';

interface UseIntersectionObserverOptions {
  threshold?: number;
}

/**
 * Custom hook to track if an element is visible in the viewport using Intersection Observer
 * @param element - The DOM element to observe
 * @param options - Configuration options for the intersection observer
 * @returns boolean indicating if the element is visible
 */
export const useIntersectionObserver = (
  element: HTMLElement | null,
  options: UseIntersectionObserverOptions = {},
): boolean => {
  const [isVisible, setIsVisible] = useState(false);
  const { threshold = 0.1 } = options;

  useEffect(() => {
    if (!element) return;

    // Check if element is already in view initially
    const initialEntry = element.getBoundingClientRect();
    if (initialEntry.top >= 0 && initialEntry.bottom <= window.innerHeight) {
      setIsVisible(true);
      return;
    }

    // Set up intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsVisible(entry.isIntersecting);
      },
      { threshold },
    );

    observer.observe(element);

    // Cleanup
    return () => {
      observer.unobserve(element);
    };
  }, [element, threshold]);

  return isVisible;
};
